import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge } from 'rxjs';

interface ActivityChannelMessage {
  readonly type: 'activity';
  readonly timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class ActivityService implements OnDestroy {
  readonly #activity = new Subject<number>();
  readonly activity$ = this.#activity.asObservable();

  #activitySubscription?: Subscription;
  #activityChannel: BroadcastChannel | null = null;
  #listenersStarted = false;

  constructor(private readonly ngZone: NgZone) {
    this.#activityChannel = typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel('activity_channel');
    this.initializeLocalStorage();
    this.setupActivityListeners();
    this.listenForActivity();
  }

  initializeLocalStorage(): void {
    if (!localStorage.getItem('lastActivity')) {
      localStorage.setItem('lastActivity', Date.now().toString());
    }
  }

  setupActivityListeners(): void {
    if (this.#listenersStarted) return;
    this.#listenersStarted = true;

    this.ngZone.runOutsideAngular(() => {
      this.#activitySubscription = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'click'),
        fromEvent(window, 'touchstart'),
        fromEvent(window, 'touchend'),
      ).subscribe(() => this.ngZone.run(() => this.notifyActivity()));
    });
  }

  notifyActivity(timestamp = Date.now()): void {
    if (!Number.isSafeInteger(timestamp) || timestamp <= 0) return;
    this.#recordActivity(timestamp);
    this.#activityChannel?.postMessage({ type: 'activity', timestamp } satisfies ActivityChannelMessage);
  }

  acceptFrameActivity(timestamp: number): void {
    this.notifyActivity(timestamp);
  }

  private listenForActivity(): void {
    if (!this.#activityChannel) return;
    this.#activityChannel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (!isActivityChannelMessage(message)) return;
      this.#recordActivity(message.timestamp);
    };
  }

  #recordActivity(timestamp: number): void {
    const current = Number(localStorage.getItem('lastActivity') ?? 0);
    if (timestamp >= current) {
      localStorage.setItem('lastActivity', timestamp.toString());
    }
    this.#activity.next(Math.max(timestamp, current));
  }

  ngOnDestroy(): void {
    this.#activitySubscription?.unsubscribe();
    this.#activityChannel?.close();
    this.#activity.complete();
  }
}

function isActivityChannelMessage(value: unknown): value is ActivityChannelMessage {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return record['type'] === 'activity' &&
    typeof record['timestamp'] === 'number' &&
    Number.isSafeInteger(record['timestamp']) && record['timestamp'] > 0;
}
